<template>
  <v-dialog v-model="isOpen" width="50%" persistent>
    <template v-slot:activator="activatorProps">
      <slot name="activator" v-bind="activatorProps" />
    </template>
    <v-card>
      <v-card-title>
        <span class="headline">{{ $t('post_proposal') }}</span>
      </v-card-title>
      <v-card-text>
        <div class="d-flex flex-column">
          <v-form ref="form" v-model="valid" class="d-flex flex-column">
            <v-select
              v-model="businessType"
              name="business_type"
              :items="businessTypes"
              :label="$t('Business_type')"
              :rules="[$rules.required]"
              required
              clearable
            >
            </v-select>
            <v-text-field
              v-model="title"
              name="title"
              :label="$t('Business_case_title')"
              :rules="[$rules.required]"
              required
            >
            </v-text-field>
            <v-textarea v-model="description" name="description" :label="$t('Wording')"> </v-textarea>
            <template v-if="$config.is_solothurn">
              <v-textarea v-model="reason" name="reason" :label="$t('Business_case_reason')" />
              <v-row>
                <v-col cols="12" md="6">
                  <v-autocomplete
                    v-model="proposal_creator"
                    name="proposal_creator"
                    :items="committeeMembers"
                    :label="$t('Business_case_proposal_creator')"
                    :rules="[$rules.required]"
                    required
                    :disabled="!user.can_create_proposals_for_other_people"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-autocomplete
                    v-model="second_signature"
                    name="second_signature"
                    :items="committeeMembers"
                    :label="$t('Business_case_second_signature')"
                    clearable
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-autocomplete
                    v-model="third_signature"
                    name="third_signature"
                    :items="committeeMembers"
                    :label="$t('Business_case_third_signature')"
                    clearable
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="rights_holder"
                    name="rights_holder"
                    :items="rightsHolderOptions"
                    :label="$t('Business_case_rights_holder')"
                    clearable
                  />
                </v-col>
              </v-row>
            </template>
            <div class="d-flex">
              <v-checkbox
                v-model="isUrgent"
                :value="true"
                name="is_urgent"
                class="mr-3"
                :label="$t('mark_urgent')"
              ></v-checkbox>
              <v-checkbox
                v-if="!$config.is_solothurn"
                v-model="hasSignatureSheet"
                :value="true"
                name="has_signature_sheet"
                :label="$t('mark_signature_sheet')"
              ></v-checkbox>
            </div>
            <p class="body-2">{{ $t('proposal_form_attachment_label') }}</p>
            <div v-for="(file, i) in files" :key="`file-${i}`" class="d-flex">
              <input
                ref="fileUpload"
                type="file"
                class="file-upload-file-input"
                @change="fileUploadHandler($event, i)"
              />
              <v-text-field v-model="file.file" disabled class="file-upload-file-path"> </v-text-field>
              <v-btn text class="file-upload-select-button" @click="openFileDialog(i)">
                {{ $t('Select_file') }}
              </v-btn>
            </div>
          </v-form>
          <v-alert class="alert-message" style="white-space: pre-line" :value="!!errorMessage" type="error" outlined>
            {{ errorMessage }}
          </v-alert>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn color="primary" text @click="close">{{ $t('Cancel') }}</v-btn>
        <v-btn
          color="primary"
          :disabled="!isValid"
          :loading="loading"
          raised
          data-testid="submit-ris-proposal"
          @click="postProposal"
        >
          {{ $t('post_proposal') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapFields } from 'vuex-map-fields'
import { mapActions } from 'vuex'
import { api } from '@/api'
import { createLink } from '@/api/helpers'
import Committee from '@/store/models/committee'
import User from '@/store/models/user'
import isEmpty from 'lodash/isEmpty'
import property from 'lodash/property'
import negate from 'lodash/negate'
import Profile from '@/store/models/profile'
import get from 'lodash/get'

const postProposalUrl = '/ris/post_proposal/{committee}'

export default {
  name: 'RisProposal',
  props: {
    committee: {
      type: Committee,
      required: true,
    },
  },
  data() {
    return {
      businessTypes: [],
      isOpen: false,
      loading: false,
      valid: null,
      files: [{ file: '' }, { file: '' }, { file: '' }],
      errorMessage: '',
      proposal_creator: Profile.query().first().username,
      rightsHolderOptions: [
        { text: this.$t('Business_case_proposal_creator'), value: 'proposal_creator' },
        { text: this.$t('Business_case_faction'), value: 'faction' },
        { text: this.$t('Business_case_commission'), value: 'commission' },
        { text: this.$t('Business_case_cross_party'), value: 'cross_party' },
      ],
    }
  },
  computed: {
    ...mapFields('risproposal', [
      'title',
      'description',
      'reason',
      'businessType',
      'isUrgent',
      'hasSignatureSheet',
      'second_signature',
      'third_signature',
      'rights_holder',
    ]),
    user() {
      return Profile.query().first()
    },
    isValid() {
      const hasFiles = this.files.map(property('file')).some(negate(isEmpty))
      const descriptionValid = !isEmpty(this.description) || hasFiles
      return this.valid && descriptionValid
    },
    committeeMembers() {
      const users = User.all().filter((user) => user.roles.includes('member'))
      return users.map((user) => ({
        text: [user.first_name, user.last_name].join(' ') + ` (${user.username})`,
        value: user.username,
      }))
    },
  },
  async mounted() {
    await this.getBusinessTypes()
  },
  methods: {
    ...mapActions({
      reset: 'risproposal/reset',
    }),
    async getBusinessTypes() {
      const businessTypes = await api.get(createLink('businesstypes'), { params: { show_for_proposal: true } })
      this.businessTypes = businessTypes.data.map((businesstype) => ({
        text: businesstype.title,
        value: businesstype.api_uid,
      }))
    },
    openFileDialog(index) {
      // Open the file dialog of the correct upload field.
      this.$refs.fileUpload[index].click()
    },
    fileUploadHandler(e, index) {
      // Set the filename in the correct display field.
      const { files } = e.target
      this.files[index].file = files.length ? files[0].name : ''
    },
    close() {
      // Close the overlay, reset file fields and remove error messages.
      this.isOpen = false
      /* eslint-disable no-param-reassign */
      this.$refs.fileUpload.forEach((f) => {
        f.value = ''
      })
      this.files.forEach((f) => {
        f.file = ''
      })
      /* eslint-enable no-param-reassign */
      this.errorMessage = ''
      this.loading = false
    },
    async postProposal() {
      if (!this.valid) {
        return
      }
      const formData = new FormData(this.$refs.form.$el)
      this.getFiles().forEach((f) => {
        formData.append('files', f)
      })
      this.loading = true
      try {
        // Post to RIS.
        const response = await api.post(createLink(postProposalUrl, { committee: this.committee.id }), formData)

        // If the response is successful but does not contain an object with key "title", the WAF is
        // intercepting the request and blocking it with status code 200 (!) ...
        if (response.data.title === undefined) {
          const error = new Error(response.data)
          error.type = 'WAFException'
          throw error
        }

        // If successful, reset localStorage and close overlay.
        this.$emit('posted', response)
        this.reset()
        this.close()
      } catch (error) {
        const nonFieldErrors = get(error, 'response.data.non_field_errors', []).join(' ')
        const messageDetail = get(error, 'response.data.detail', null)
        const messageToDisplay = messageDetail || nonFieldErrors

        this.notifyError(this.$t('SubmitVorstossCardSubmitError', { message: messageToDisplay }))
        this.loading = false
        throw error
      }
    },
    getFiles() {
      // Return an array of files (non-empty file fields).
      if (this.$refs.fileUpload) {
        return this.$refs.fileUpload.map((f) => (f.files.length ? f.files[0] : null)).filter((f) => f !== null)
      }
      return []
    },
  },
}
</script>

<style lang="scss" scoped>
.file-upload-select-button {
  position: relative;
  overflow: hidden;
  margin: 10px 0 10px 10px;
}
.file-upload-file-input {
  display: none;
}
</style>
